import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { IDivision } from '@src/models/interfaces/division.interface';
import { actions, selectAreAllDivisionsSelected } from '@src/packages/divisions';
import { resolveSelectedDivisionIds } from '@src/packages/divisions/utils/resolveSelectedDivisionIds';

const ALL_DIVS = 'all';

const sortDivisions = (a: IDivision, b: IDivision) =>
  (a?.name || '').localeCompare(b?.name || '', undefined, {
    numeric: true,
    sensitivity: 'base',
  });

const areDivisionSelectionsEqual = (left: string[], right: string[]) =>
  left.length === right.length && left.every((id, index) => id === right[index]);

export const useDivisionSelector = () => {
  const dispatch = useDispatch();

  const { id: selectedOrgId, loading: orgLoading = false } = useSelector(
    ({ organizations }: any) => organizations.organization,
  );
  const assignedData = useSelector(
    ({ divisions }: any) => divisions.assigned?.data ?? { divisions: {} },
  );
  const selectedDivs: string[] = useSelector(({ divisions }: any) => divisions.selector.ids || []);
  const allSelected = useSelector(selectAreAllDivisionsSelected);

  const userDivisions = Object.values(assignedData.divisions || []) as IDivision[];
  const allDivsIds = userDivisions.map(div => div.id);

  /**
   * Keep the filter aligned with assigned divisions (org switch, create/delete, backend sync).
   */
  useEffect(() => {
    if (!allDivsIds.length) {
      return;
    }

    const savedDivs = localStorage.getItem('selectedDivs')?.split(',').filter(Boolean) || [];
    const nextDivs = resolveSelectedDivisionIds(allDivsIds, selectedDivs, savedDivs);

    if (!areDivisionSelectionsEqual(nextDivs, selectedDivs)) {
      dispatch(actions.divisionsSelect(nextDivs));
    }
  }, [allDivsIds.join(','), dispatch, selectedDivs, selectedOrgId]);

  const onChangeDivision = (value: string[]) => {
    const lastValue = [...value].pop();
    let divs: string[];

    if (lastValue === ALL_DIVS) {
      divs = allDivsIds;
    } else {
      const divValues = value.filter(i => i !== ALL_DIVS);
      divs = divValues.length ? divValues : allDivsIds;
    }

    dispatch(actions.divisionsSelect(divs));
  };

  return {
    ALL_DIVS,
    allSelected,
    onChangeDivision,
    orgLoading,
    selectedDivs,
    sortedDivisions: userDivisions.sort(sortDivisions),
  };
};
